import { useState, useEffect } from 'react';
import { Cpu, AlertCircle, X, LogOut, User, Shield, Lock } from 'lucide-react';
import { auth } from './lib/auth';
import { getEnvironments, getDefaultEnvironment, getEnvironmentByApiUrl } from './lib/environments';
import type { Environment } from './lib/environments';
import { createApiService, DEFAULT_SERVER_URL } from './lib/api';
import type { ApiService } from './lib/api';
import { ConfigPanel, DeviceDetail, DeviceList, FirmwarePanel, ServerSwitcher } from './components';

const APP_VERSION = __APP_VERSION__;
const HAS_ENVIRONMENTS = getEnvironments().length > 0;

type AuthState = 'loading' | 'login' | 'authenticated' | 'error';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeviceMap = Record<string, any>;

export default function App() {
  // Auth state
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [authError, setAuthError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<Record<string, unknown> | null>(null);
  const [activeEnvKey, setActiveEnvKey] = useState<string | null>(null);

  // App state
  const [serverUrl, setServerUrl] = useState(DEFAULT_SERVER_URL);
  const [connected, setConnected] = useState(false);
  const [devices, setDevices] = useState<DeviceMap>({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deviceData, setDeviceData] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState({ devices: false, device: false, more: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [api, setApi] = useState<ApiService | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Legacy API key mode state
  const [config, setConfig] = useState({ url: DEFAULT_SERVER_URL, key: '' });

  // ----------------------------------------------------------------
  // OAuth initialization
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!HAS_ENVIRONMENTS) {
      setAuthState('login');
      return;
    }

    const initAuth = async () => {
      try {
        const result = await auth.init();
        if (result.authenticated) {
          setUserInfo(auth.getUserInfo());
          setActiveEnvKey(result.environmentKey || null);

          const activeConfig = auth.getActiveConfig();
          if (activeConfig?._envKey) {
            const env = getEnvironmentByApiUrl(
              getEnvironments().find((e) => e.key === activeConfig._envKey)?.apiUrl || ''
            );
            if (env) setServerUrl(env.apiUrl);
          }

          setAuthState('authenticated');
        } else {
          setAuthState('login');
        }
      } catch (err) {
        console.error('[Auth] Init error:', err);
        setAuthError((err as Error).message);
        setAuthState('error');
      }
    };

    initAuth();

    const unsub = auth.onAuthChange((event) => {
      if (event.type === 'logout') {
        setAuthState('login');
        setConnected(false);
        setApi(null);
        setDevices({});
        setUserInfo(null);
        setActiveEnvKey(null);
      }
      if (event.type === 'token_refreshed') {
        setUserInfo(auth.getUserInfo());
      }
    });

    return unsub;
  }, []);

  // ----------------------------------------------------------------
  // Auto-connect after OAuth authentication
  // ----------------------------------------------------------------
  useEffect(() => {
    if (HAS_ENVIRONMENTS && authState === 'authenticated' && !connected) {
      connectWithOAuth(serverUrl);
    }
  }, [authState]);

  // ----------------------------------------------------------------
  // Connection handlers
  // ----------------------------------------------------------------

  const connectWithOAuth = async (url: string) => {
    const apiService = createApiService(url, () => auth.getAccessToken());
    setApi(apiService);
    setLoading((l) => ({ ...l, devices: true }));
    setError(null);
    try {
      const data = await apiService.getAllDevices();
      const { cursor: nextCursor, ...devData } = data;
      setDevices(devData);
      setCursor(nextCursor || null);
      setConnected(true);
      setServerUrl(url);
    } catch (e) {
      if ((e as Error).message.includes('Unauthorized')) {
        setAuthError('Session expired or unauthorized. Please log in again.');
        setAuthState('error');
      } else {
        setError(`Connection failed: ${(e as Error).message}`);
      }
    }
    setLoading((l) => ({ ...l, devices: false }));
  };

  const connectWithApiKey = async (cfg: { url: string; key: string }) => {
    if (!cfg.url || !cfg.key) {
      setError('Please provide server URL and API key');
      return;
    }
    const apiService = createApiService(cfg.url, cfg.key);
    setApi(apiService);
    setLoading((l) => ({ ...l, devices: true }));
    setError(null);
    try {
      const data = await apiService.getAllDevices();
      const { cursor: nextCursor, ...devData } = data;
      setDevices(devData);
      setCursor(nextCursor || null);
      setConnected(true);
      setServerUrl(cfg.url);
    } catch (e) {
      setError(`Connection failed: ${(e as Error).message}`);
    }
    setLoading((l) => ({ ...l, devices: false }));
  };

  // ----------------------------------------------------------------
  // Server switching
  // ----------------------------------------------------------------

  const switchServer = async (newUrl: string) => {
    if (!newUrl) return;
    const tokenOrKey = HAS_ENVIRONMENTS ? () => auth.getAccessToken() : config.key;
    const apiService = createApiService(newUrl, tokenOrKey);

    setLoading((l) => ({ ...l, devices: true }));
    setError(null);
    try {
      const data = await apiService.getAllDevices();
      const { cursor: nextCursor, ...devData } = data;
      setApi(apiService);
      setServerUrl(newUrl);
      setDevices(devData);
      setCursor(nextCursor || null);
      setSelectedDevice(null);
      setDeviceData(null);
    } catch (e) {
      setError(`Failed to connect to ${newUrl}: ${(e as Error).message}`);
    }
    setLoading((l) => ({ ...l, devices: false }));
  };

  const switchWithReauth = (envConfig: Environment) => {
    auth.logout({ redirect: false });
    setConnected(false);
    setApi(null);
    setDevices({});
    setSelectedDevice(null);
    setDeviceData(null);
    setServerUrl(envConfig.apiUrl);
    setActiveEnvKey(envConfig.key);

    auth.login(envConfig.cognito, envConfig.key);
  };

  // ----------------------------------------------------------------
  // Device operations
  // ----------------------------------------------------------------

  const refreshDevices = async () => {
    if (!api) return;
    setLoading((l) => ({ ...l, devices: true }));
    try {
      const data = await api.getAllDevices();
      const { cursor: nextCursor, ...devData } = data;
      setDevices(devData);
      setCursor(nextCursor || null);
    } catch (e) {
      setError(`Failed to refresh: ${(e as Error).message}`);
    }
    setLoading((l) => ({ ...l, devices: false }));
  };

  const loadMoreDevices = async () => {
    if (!api || !cursor || loading.more) return;
    setLoading((l) => ({ ...l, more: true }));
    try {
      const data = await api.getAllDevices(cursor);
      const { cursor: nextCursor, ...devData } = data;
      setDevices((prev) => ({ ...prev, ...devData }));
      setCursor(nextCursor || null);
    } catch (e) {
      setError(`Failed to load more: ${(e as Error).message}`);
    }
    setLoading((l) => ({ ...l, more: false }));
  };

  const selectDevice = async (uuid: string) => {
    setSelectedDevice(uuid);
    if (!api) return;
    setLoading((l) => ({ ...l, device: true }));
    try {
      const data = await api.getDevice(uuid);
      setDeviceData(data);
      setDevices((prev) => (prev[uuid] ? prev : { ...prev, [uuid]: data }));
    } catch (e) {
      setError(`Failed to load device: ${(e as Error).message}`);
    }
    setLoading((l) => ({ ...l, device: false }));
  };

  const refreshDevice = async (uuid: string) => {
    if (!api) return;
    setLoading((l) => ({ ...l, device: true }));
    try {
      const data = await api.getDevice(uuid);
      setDeviceData(data);
      setDevices((prev) => ({ ...prev, [uuid]: data }));
    } catch (e) {
      setError(`Failed to refresh device: ${(e as Error).message}`);
    }
    setLoading((l) => ({ ...l, device: false }));
  };

  // ----------------------------------------------------------------
  // Login / Logout
  // ----------------------------------------------------------------

  const handleLogin = (envConfig: Environment | null) => {
    if (envConfig?.cognito) {
      auth.login(envConfig.cognito, envConfig.key);
    }
  };

  const handleLogout = () => {
    if (HAS_ENVIRONMENTS) {
      auth.logout();
    } else {
      setConnected(false);
      setApi(null);
      setDevices({});
      setConfig({ url: DEFAULT_SERVER_URL, key: '' });
    }
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  if (HAS_ENVIRONMENTS && authState === 'loading') {
    return (
      <div className="h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-400">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex flex-col">
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cpu size={28} className="text-blue-500" />
            <h1 className="text-xl font-bold">Sandpiper Management</h1>
            <span className="text-xs text-gray-500 self-end mb-0.5">v{APP_VERSION}</span>
          </div>
          <div className="flex items-center gap-4">
            {connected && (
              <ServerSwitcher
                currentUrl={serverUrl}
                currentEnvKey={activeEnvKey}
                onSwitch={switchServer}
                onReauth={switchWithReauth}
                loading={loading.devices}
              />
            )}
            {HAS_ENVIRONMENTS && authState === 'authenticated' && userInfo && (
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <User size={14} />
                <span className="hidden md:inline">{(userInfo.email as string) || 'Authenticated'}</span>
              </div>
            )}
            {(connected || (HAS_ENVIRONMENTS && authState === 'authenticated')) && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700"
                title="Logout"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {(error || authError) && (
        <div className="bg-red-900/80 border-b border-red-700 px-6 py-2 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-red-200 text-sm">
            <AlertCircle size={16} /> {error || authError}
          </div>
          <button
            onClick={() => { setError(null); setAuthError(null); }}
            className="text-red-300 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <main className="flex-1 p-4 overflow-hidden">
        {!connected ? (
          <div className="max-w-md mx-auto mt-20">
            {HAS_ENVIRONMENTS ? (
              <OAuthLoginPanel
                authState={authState}
                authError={authError}
                serverUrl={serverUrl}
                setServerUrl={setServerUrl}
                onLogin={handleLogin}
                onConnect={connectWithOAuth}
                loading={loading.devices}
              />
            ) : (
              <ConfigPanel
                config={config}
                setConfig={setConfig}
                onConnect={connectWithApiKey}
                loading={loading.devices}
              />
            )}
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4 h-full">
            <div className="col-span-12 lg:col-span-2">
              <FirmwarePanel api={api} />
            </div>
            <div className="col-span-12 lg:col-span-3 h-full min-h-0">
              <DeviceList
                devices={devices}
                cursor={cursor}
                selectedDevice={selectedDevice}
                onSelect={selectDevice}
                onLookup={selectDevice}
                loading={loading.devices}
                loadingMore={loading.more}
                onRefresh={refreshDevices}
                onLoadMore={loadMoreDevices}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <div className="col-span-12 lg:col-span-7 h-full min-h-0">
              <DeviceDetail
                uuid={selectedDevice}
                device={devices[selectedDevice ?? '']}
                deviceData={deviceData}
                api={api}
                onRefresh={refreshDevice}
                loading={loading.device}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ================================================================
// OAuth Login Panel
// ================================================================

interface OAuthLoginPanelProps {
  authState: AuthState;
  authError: string | null;
  serverUrl: string;
  setServerUrl: (url: string) => void;
  onLogin: (envConfig: Environment | null) => void;
  onConnect: (url: string) => void;
  loading: boolean;
}

function OAuthLoginPanel({ authState, authError, serverUrl, setServerUrl, onLogin, onConnect, loading }: OAuthLoginPanelProps) {
  const environments = getEnvironments();
  const defaultEnv = getDefaultEnvironment();
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(defaultEnv);

  if (authState === 'authenticated') {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield size={24} className="text-green-500" />
          <div>
            <h2 className="text-lg font-semibold">Authenticated</h2>
            <p className="text-sm text-gray-400">Configure the API server to connect</p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-1">API Server URL</label>
          <input
            type="url"
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            placeholder="https://manage.sandpiper.mytransit.info"
          />
        </div>

        <button
          onClick={() => onConnect(serverUrl)}
          disabled={loading || !serverUrl}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded px-4 py-2 text-sm font-medium flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Connecting...
            </>
          ) : (
            'Connect'
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Shield size={24} className="text-blue-500" />
        <div>
          <h2 className="text-lg font-semibold">Sandpiper Management</h2>
          <p className="text-sm text-gray-400">Sign in to manage devices</p>
        </div>
      </div>

      {authError && (
        <div className="bg-red-900/50 border border-red-700 rounded p-3 mb-4 text-sm text-red-200">
          {authError}
        </div>
      )}

      {environments.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-1">Environment</label>
          <select
            value={selectedEnv?.key || ''}
            onChange={(e) => {
              const env = environments.find((env) => env.key === e.target.value);
              setSelectedEnv(env || null);
            }}
            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
          >
            {environments.map((env) => (
              <option key={env.key} value={env.key} disabled={!env.cognito.domain}>
                {env.label}
                {!env.cognito.domain ? ' (auth not configured)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <button
        onClick={() => onLogin(selectedEnv)}
        disabled={!selectedEnv?.cognito?.domain}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded px-4 py-2 text-sm font-medium flex items-center justify-center gap-2"
      >
        <Lock size={16} />
        {selectedEnv?.cognito?.identityProvider === 'IAMIdentityCenter'
          ? `Sign in to ${selectedEnv?.label || 'environment'} via IAM Identity Center`
          : `Sign in to ${selectedEnv?.label || 'environment'}`}
      </button>

      <p className="text-xs text-gray-500 mt-4 text-center">
        You will be redirected to sign in securely.
      </p>
    </div>
  );
}
